import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Apple, Globe, Download, Share } from 'lucide-react';
import { IOS_APP_URL, isAndroid } from '@/lib/platform';
import { useInstallPrompt } from '@/lib/pwaInstall';

// Platform-aware choice shown when a visitor picks a plan:
//  - iOS: download the native App Store app, or continue in the browser
//  - Android: install the web app (PWA), or continue in the browser
export function AppChoiceModal({ open, onOpenChange }) {
  const navigate = useNavigate();
  const android = isAndroid();
  const { canInstall, promptInstall } = useInstallPrompt();

  const goRegister = () => { onOpenChange(false); navigate('/register'); };

  const handleInstall = async () => {
    const result = await promptInstall();
    if (result === 'prompted') onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ios-app-choice-modal" className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Get the best experience</DialogTitle>
          <DialogDescription>
            {android
              ? 'Install The Tiffin app on your phone, or continue right here in your browser.'
              : 'Download The Tiffin app for iPhone & iPad, or continue right here in your browser.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-2">
          {android ? (
            canInstall ? (
              <Button
                data-testid="android-install-app-btn"
                onClick={handleInstall}
                className="w-full h-12 rounded-full bg-obsidian text-white hover:bg-obsidian/90 font-semibold gap-2"
              >
                <Download size={18} /> Install our app
              </Button>
            ) : (
              <div
                data-testid="android-install-tip"
                className="rounded-2xl bg-bone border border-border/50 p-3.5 flex items-start gap-2.5 text-sm text-charcoal"
              >
                <Share size={18} className="text-saffron shrink-0 mt-0.5" />
                <span>
                  To install the app, open your browser menu and tap{' '}
                  <span className="font-semibold">“Add to Home screen”</span>.
                </span>
              </div>
            )
          ) : (
            <a
              href={IOS_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
              data-testid="ios-download-app-btn"
            >
              <Button className="w-full h-12 rounded-full bg-obsidian text-white hover:bg-obsidian/90 font-semibold gap-2">
                <Apple size={18} /> Download our app
              </Button>
            </a>
          )}

          <Button
            data-testid="ios-continue-browser-btn"
            variant="outline"
            onClick={goRegister}
            className="w-full h-12 rounded-full font-semibold gap-2"
          >
            <Globe size={18} /> Continue on Browser
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
